import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  AI_ASSIST_NODE_TYPES,
  applyNodeAssistSuggestions,
  canAssistForNode,
  extractSamplePayload,
  fetchNodeAssist,
  NODE_ASSIST_LABELS,
} from '../utils/aiNodeSuggest';

export function AiNodeAssist({
  nodeType,
  node,
  config,
  nodes,
  edges,
  liveMessages,
  mqttConnected,
  onApply,
}) {
  const [loading, setLoading] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [hint, setHint] = useState('');
  const [explanation, setExplanation] = useState('');
  const [suggestionCount, setSuggestionCount] = useState(0);
  const lastAutoKey = useRef('');

  const sample = extractSamplePayload(
    node?.id,
    nodeType,
    config,
    nodes,
    edges,
    liveMessages,
  );
  const samplePayload = sample?.payload ?? null;
  const topic = sample?.topic || '';
  const canExplain = canAssistForNode(config, liveMessages, sample);
  const canSuggest = canAssistForNode(config, liveMessages, sample, { requireSample: true });
  const label = NODE_ASSIST_LABELS[nodeType] || 'configuration';
  const supported = AI_ASSIST_NODE_TYPES.has(nodeType);

  const runAssist = useCallback(async ({ explainOnly = false, auto = false } = {}) => {
    if (!supported) return;
    if (explainOnly) setExplaining(true);
    else setLoading(true);

    try {
      if (!auto) {
        toast.loading(explainOnly ? 'Generating explanation…' : `Analyzing live data for ${label}…`, {
          id: 'ai-assist',
        });
      }

      const result = await fetchNodeAssist({
        targetNode: nodeType,
        samplePayload,
        topic,
        config,
        userHint: hint,
        explainOnly,
      });

      setExplanation(result.explanation || '');

      if (!explainOnly && result.suggestions) {
        const keys = Object.keys(result.suggestions).filter(
          (k) => result.suggestions[k] != null && result.suggestions[k] !== '',
        );
        setSuggestionCount(keys.length);
        if (keys.length > 0) {
          applyNodeAssistSuggestions(result.suggestions, onApply, nodeType);
          if (!auto) {
            toast.success(`AI suggested ${keys.length} field(s) for ${label}`, { id: 'ai-assist' });
          }
        } else if (!auto) {
          toast.success('AI explanation ready', { id: 'ai-assist' });
        }
      } else if (!auto) {
        toast.success('AI explanation ready', { id: 'ai-assist' });
      }
    } catch (error) {
      if (!auto) {
        toast.error(error.message || 'AI assist failed', { id: 'ai-assist' });
      }
    } finally {
      setLoading(false);
      setExplaining(false);
    }
  }, [supported, nodeType, samplePayload, topic, config, hint, label, onApply]);

  // Auto-explain when node selection or live sample changes
  useEffect(() => {
    if (!supported || !canExplain) return undefined;

    const autoKey = `${node?.id}:${nodeType}:${samplePayload ? JSON.stringify(samplePayload).slice(0, 200) : 'none'}`;
    if (autoKey === lastAutoKey.current) return undefined;
    lastAutoKey.current = autoKey;

    const timer = setTimeout(() => {
      runAssist({ explainOnly: true, auto: true });
    }, 600);

    return () => clearTimeout(timer);
  }, [node?.id, nodeType, supported, canExplain, samplePayload, runAssist]);

  useEffect(() => {
    setHint('');
    setExplanation('');
    setSuggestionCount(0);
    lastAutoKey.current = '';
  }, [node?.id, nodeType]);

  if (!supported) return null;

  return (
    <div className="config-field ai-node-assist" style={{ marginBottom: 12 }}>
      <div className="config-section-header" style={{ marginBottom: 8 }}>
        <span className="ai-sparkle">✨</span>
        AI assist
      </div>

      {explanation && (
        <div
          className="ai-explanation"
          style={{
            padding: '10px 12px',
            marginBottom: 10,
            borderRadius: 8,
            background: 'var(--config-surface, rgba(0,0,0,0.04))',
            fontSize: '0.8125rem',
            lineHeight: 1.5,
          }}
          data-testid={`ai-explanation-${nodeType}`}
        >
          {explaining ? 'Analyzing node and live data…' : explanation}
        </div>
      )}

      {!explanation && explaining && (
        <p className="config-hint" style={{ marginBottom: 10 }}>Analyzing node and live data…</p>
      )}

      <Label>Optional: what should this node do?</Label>
      <Textarea
        value={hint}
        onChange={(e) => setHint(e.target.value)}
        placeholder="e.g. only keep strong RSSI, map fields for Supabase, publish to alerts/temp"
        className="config-input config-textarea"
        rows={2}
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canExplain || explaining}
          onClick={() => runAssist({ explainOnly: true })}
          data-testid={`ai-explain-${nodeType}`}
        >
          {explaining ? 'Explaining…' : 'Explain'}
        </Button>
        <Button
          type="button"
          className="ai-generate-btn"
          size="sm"
          disabled={!canSuggest || loading}
          onClick={() => runAssist({ explainOnly: false })}
          data-testid={`ai-suggest-${nodeType}`}
        >
          {loading ? 'Suggesting…' : `Suggest ${label}`}
        </Button>
      </div>

      {suggestionCount > 0 && (
        <span className="config-hint" style={{ display: 'block', marginTop: 6 }}>
          Last run applied {suggestionCount} suggested field(s). Review and click Apply Changes.
        </span>
      )}

      {!mqttConnected && (
        <span className="config-hint" style={{ display: 'block', marginTop: 6 }}>
          Click Run to stream live MQTT data for better suggestions.
        </span>
      )}
      {mqttConnected && !samplePayload && (
        <span className="config-hint" style={{ display: 'block', marginTop: 6 }}>
          Waiting for upstream messages — wire Gateway → this node and ensure data is publishing.
        </span>
      )}
    </div>
  );
}
